import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PIPELINE_STAGES } from '../PipelineNav';
import {
  approveStage,
  setStageComment,
  subscribe,
  getProject,
} from '../../../services/pipelineProject.service';
import '../Pipeline.css';

/**
 * PipelineShell
 *
 * Generic wrapper for every pipeline stage.  Provides:
 * - Page header with stage icon, title, description
 * - Optional right-hand action slot (e.g. "Generate" button)
 * - Scrollable body for stage-specific content (children)
 * - Approval footer: comment textarea → Approve/Revoke button → Continue
 *
 * Props:
 *   stageId      – current stage id ('requirements' | 'design' | …)
 *   title        – optional override for stage label
 *   description  – subtitle shown below the title
 *   headerAction – JSX node rendered in the header-right slot
 *   canApprove   – whether output has been generated (enables approve)
 *   children     – main content
 */
function PipelineShell({ stageId, title, description, headerAction, canApprove = false, children }) {
  const navigate = useNavigate();
  const [project, setProject] = useState(getProject());

  useEffect(() => subscribe(setProject), []);

  const stageIdx  = PIPELINE_STAGES.findIndex((s) => s.id === stageId);
  const stage     = stageIdx >= 0 ? PIPELINE_STAGES[stageIdx] : null;
  const nextStage = stageIdx < PIPELINE_STAGES.length - 1 ? PIPELINE_STAGES[stageIdx + 1] : null;

  const stageState = project?.stages?.[stageId] || {};
  const approved   = Boolean(stageState.approved);
  const comment    = stageState.comment || '';

  const commentRef = useRef(null);

  const handleCommentChange = (e) => {
    setStageComment(stageId, e.target.value);
  };

  const handleApprove = () => {
    approveStage(stageId, !approved, comment);
    if (!approved && nextStage) {
      // Auto-navigate to next stage after a short delay
      setTimeout(() => navigate(`/tools/pipeline/${nextStage.id}`), 300);
    }
  };

  const continueDisabled = !approved;

  return (
    <div className="pipeline-stage">
      {/* ── Header ──────────────────────────────────────────────────────── */}
      <header className="pipeline-stage-header">
        <div>
          <h2 className="pipeline-stage-title">
            {stage?.icon} {title || stage?.label}
          </h2>
          {description && <p className="pipeline-stage-desc">{description}</p>}
        </div>
        {headerAction && <div>{headerAction}</div>}
      </header>

      {/* ── Body ────────────────────────────────────────────────────────── */}
      <div className="pipeline-stage-body">{children}</div>

      {/* ── Approval footer ─────────────────────────────────────────────── */}
      <footer className="pipeline-approval">
        {approved ? (
          <div className="pipeline-approved-badge">
            ✅ Approved
          </div>
        ) : null}

        <textarea
          ref={commentRef}
          className="pipeline-approval-comment"
          placeholder="Optional comment or revision request…"
          value={comment}
          onChange={handleCommentChange}
          disabled={approved}
          rows={1}
        />

        <button
          className={`pipeline-btn ${approved ? 'secondary' : 'success'}`}
          onClick={handleApprove}
          disabled={!canApprove && !approved}
          title={approved ? 'Revoke approval' : 'Approve this stage output'}
        >
          {approved ? '↩ Revoke' : '✓ Approve'}
        </button>

        {nextStage && (
          <button
            className="pipeline-btn primary"
            disabled={continueDisabled}
            onClick={() => navigate(`/tools/pipeline/${nextStage.id}`)}
          >
            Continue → {nextStage.label}
          </button>
        )}

        {!nextStage && stageId === 'deployment' && (
          <span style={{ fontSize: '0.88rem', color: '#64748b' }}>
            Pipeline complete 🎉
          </span>
        )}
      </footer>
    </div>
  );
}

export default PipelineShell;
